import axios from 'axios'
import md5 from 'js-md5'
import { getToken } from '@/utils/auth'
import {
  ensureAdminTokenFresh,
  getCurrentAdminToken,
  isRefreshTokenRequest,
  redirectToLogin,
  refreshAdminToken,
} from '@/utils/admin-token'
import store from '@/store'
import router from '@/router'
import Vue from 'vue'


// 定义缓存池用来缓存数据
const cache = {}
const EXPIRE_TIME = 20000

const CancelToken = axios.CancelToken
// create an axios instance
const streamer = axios.create({
  // baseURL: 'http://192.168.1.61:8984', // url = base url + request url
  baseURL: 'https://danmuadmin.wokfoy.com', // url = base url + request url
  // baseURL: process.env.NODE_ENV === 'development' ? process.env.VUE_APP_BASE_API : 'http://ubuntu:8081', // url = base url + request url
  // withCredentials: true, // send cookies when cross-domain requests
  timeout: 60000, // request timeout
  withCredentials: false,
})

const viewer = axios.create({
  baseURL: 'https://danmuserver.wokfoy.com', // url = base url + request url
  // baseURL: process.env.NODE_ENV === 'development' ? process.env.VUE_APP_BASE_API : 'http://ubuntu:8081', // url = base url + request url
  // withCredentials: true, // send cookies when cross-domain requests
  timeout: 60000, // request timeout
  withCredentials: false,
})

const shop = axios.create({
  // baseURL: 'http://192.168.1.61:8980', // url = base url + request url
  baseURL: 'https://biadmin.wokfoy.com', // url = base url + request url
  // baseURL: process.env.NODE_ENV === 'development' ? process.env.VUE_APP_BASE_API : 'http://ubuntu:8081', // url = base url + request url
  // withCredentials: true, // send cookies when cross-domain requests
  timeout: 60000, // request timeout
  withCredentials: false,
})

const promotion = axios.create({
  baseURL: process.env.NODE_ENV === 'production' ? 'https://anal.wokfoy.com' : 'https://anal.wokfoy.com',
  timeout: 60000,
  withCredentials: false,
})

// 开发环境走同源代理，避免 HTTPS 调试页面直接访问 HTTP 本地服务造成混合内容拦截。
// 生产环境仍请求线上 Go API。
const obsGateway = axios.create({
  baseURL: process.env.NODE_ENV === 'production' ? 'https://anal.wokfoy.com' : '',
  timeout: 60000,
  withCredentials: false,
})

function isAuthBusinessCode(code) {
  return Number(code) === 20010001 || Number(code) === 3001002
}

async function attachAdminAuthorization(config) {
  config.headers = config.headers || {}
  config.headers.Accept = config.headers.Accept || 'application/json'
  if (!isRefreshTokenRequest(config)) {
    try {
      await ensureAdminTokenFresh()
    } catch (error) {
      console.warn('[Auth] proactive refresh failed, continue with current token', error)
    }
  }
  const token = getCurrentAdminToken()
  if (token) {
    config.headers.Authorization = 'Bearer ' + token
  }
  return config
}

async function handleAuthFailure(error, instance) {
  const config = error?.config || {}
  if (isRefreshTokenRequest(config) || config.__isRetryAfterRefresh) {
    await redirectToLogin()
    return Promise.reject(error)
  }
  const newToken = await refreshAdminToken()
  if (!newToken) return Promise.reject(error)
  config.__isRetryAfterRefresh = true
  config.headers = config.headers || {}
  config.headers.Authorization = 'Bearer ' + newToken
  config.headers.Accept = config.headers.Accept || 'application/json'
  return instance.request(config)
}


// request interceptor
streamer.interceptors.request.use(
  async config => {
    // do something before request is sent
    if (config.cache) {
      const source = CancelToken.source()
      config.cancelToken = source.token
      // 去缓存池获取缓存数据
      const key =
        config.method +
        config.baseURL +
        config.url +
        JSON.stringify(config.params)
      const data = cache[md5(key)]
      // 获取当前时间戳
      const expire_time = getExpireTime()
      if (data && expire_time - data.expire < EXPIRE_TIME) {
        source.cancel(data)
      }
    }
    return attachAdminAuthorization(config)
  },
  error => {
    // do something with request error
    // for debug
    return Promise.reject(error)
  }
)

// response interceptor
streamer.interceptors.response.use(
  response => {
    const res = response.data
    if (res.code !== 0) {
      if (isAuthBusinessCode(res.code)) {
        redirectToLogin()
      }
      console.log(this)
      if (!response.config?.suppressErrorNotification) {
        Vue.prototype.$notify({
         title: 'ERROR',
          message: res.message || 'Error',
          type: 'error',
        })
      }
      return Promise.reject(new Error(res.message || 'Error'))
    } else {
      return res
    }
  },
  async error => {
    // 1) 被我们用 cancelToken 命中缓存的情况
    if (axios.isCancel(error)) return Promise.resolve(error.message || error);

    // 2) 真正的网络层错误（CORS/证书/超时/DNS 等），没有 response
    if (!error.response) {
      // 常见触发：预检失败、HTTPS 页面请求 HTTP、证书异常、超时
      console.error('[Axios] Network-level error:', {
        message: error.message,
        config: { method: error.config?.method, url: (error.config?.baseURL || '') + (error.config?.url || '') },
        timeout: error.config?.timeout,
      });
      return Promise.reject(new Error('Network Error（可能是 CORS/证书/超时/网络）'));
    }

    // 3) 有 response 的话，按你的业务格式处理
    const { status, data, headers } = error.response;
    // 处理HTTP状态码
    if (status == 401) {
      return handleAuthFailure(error, streamer)
    }
    // 处理其他业务错误
    if (data && data.code !== 200 && data.code !== 0) {
      const errorMsg = data.message || 'Request failed'
      if (!config.suppressErrorNotification) {
        Vue.prototype.$notify({
          type:'error',
          title: 'ERROR',
          message: errorMsg
        })
      }
      return Promise.reject(new Error(errorMsg))
    }
    console.error('[Axios] HTTP error:', status, (error.config?.url || ''), headers, data);

    // 兼容 data 为空的情况
    const msg = (data && (data.message || data.msg)) || `HTTP ${status}`;
    return Promise.reject(new Error(msg));
  }
)
viewer.interceptors.request.use(
  config => {
    // do something before request is sent
    if (config.cache) {
      const source = CancelToken.source()
      config.cancelToken = source.token
      // 去缓存池获取缓存数据
      const key =
        config.method +
        config.baseURL +
        config.url +
        JSON.stringify(config.params)
      const data = cache[md5(key)]
      // 获取当前时间戳
      const expire_time = getExpireTime()
      if (data && expire_time - data.expire < EXPIRE_TIME) {
        source.cancel(data)
      }
    }
    let token = store.getters.token || localStorage.getItem('lvtu_view_apitoken') || getToken()
    console.log(token)
    if (token) {
      config.headers['Authorization'] = 'Bearer ' + token
    }

    return config
  },
  error => {
    // do something with request error
    // for debug
    return Promise.reject(error)
  }
)

// response interceptor
viewer.interceptors.response.use(
  response => {
    const res = response.data
    if (res.code !== 0) {
      // Vue.prototype.$notify({
      //   title: 'ERROR',
      //   message: res.message || 'Error',
      //   type: 'warning',
      // })
      return Promise.reject(new Error(res.message || 'Error'))
    } else {
      return res
    }
  },
   async error => {
    // 1) 被我们用 cancelToken 命中缓存的情况
    if (axios.isCancel(error)) return Promise.resolve(error.message || error);

    // 2) 真正的网络层错误（CORS/证书/超时/DNS 等），没有 response
    if (!error.response) {
      // 常见触发：预检失败、HTTPS 页面请求 HTTP、证书异常、超时
      console.error('[Axios] Network-level error:', {
        message: error.message,
        config: { method: error.config?.method, url: (error.config?.baseURL || '') + (error.config?.url || '') },
        timeout: error.config?.timeout,
      });
      return Promise.reject(new Error('Network Error（可能是 CORS/证书/超时/网络）'));
    }

    // 3) 有 response 的话，按你的业务格式处理
    const { status, data, headers } = error.response;
    // 处理HTTP状态码
    if (status == 401) {
      // token过期处理
      if (router.currentRoute.path !== '/signin') {
        try {
          await router.replace({
            path: '/signin',
            query: { redirect: router.currentRoute.fullPath }
          });
        } catch (navErr) {
          // 忽略重复导航等
          if (!(router.isNavigationFailure?.(navErr, 16))) {
            console.warn('router replace failed:', navErr);
          }
        }
      }
      // ✅ 关键：这里“结束链路”，不要再 reject
      return Promise.reject(error); // 或 return; 让调用方的 .then/.catch 不再收到错误
    }
    // 处理其他业务错误
    if (data && data.code !== 200 && data.code !== 0) {
      const errorMsg = data.message || 'Request failed'
      return Promise.reject(new Error(errorMsg))
    }
    console.error('[Axios] HTTP error:', status, (error.config?.url || ''), headers, data);

    // 兼容 data 为空的情况
    const msg = (data && (data.message || data.msg)) || `HTTP ${status}`;
    return Promise.reject(new Error(msg));
  }
)
shop.interceptors.request.use(
  async config => {
    // do something before request is sent
    if (config.cache) {
      const source = CancelToken.source()
      config.cancelToken = source.token
      // 去缓存池获取缓存数据
      const key =
        config.method +
        config.baseURL +
        config.url +
        JSON.stringify(config.params)
      const data = cache[md5(key)]
      // 获取当前时间戳
      const expire_time = getExpireTime()
      if (data && expire_time - data.expire < EXPIRE_TIME) {
        source.cancel(data)
      }
    }
    return attachAdminAuthorization(config)
  },
  error => {
    // do something with request error
    // for debug
    return Promise.reject(error)
  }
)

// response interceptor
shop.interceptors.response.use(
  response => {
    const res = response.data
    if (res.code !== 0) {
      if (isAuthBusinessCode(res.code)) {
        redirectToLogin()
      }
      // Vue.prototype.$notify({
      //   title: 'ERROR',
      //   message: res.message || 'Error',
      //   type: 'warning',
      // })
      return Promise.reject(new Error(res.message || 'Error'))
    } else {
      return res
    }
  },
   async error => {
    // 1) 被我们用 cancelToken 命中缓存的情况
    if (axios.isCancel(error)) return Promise.resolve(error.message || error);

    // 2) 真正的网络层错误（CORS/证书/超时/DNS 等），没有 response
    if (!error.response) {
      // 常见触发：预检失败、HTTPS 页面请求 HTTP、证书异常、超时
      console.error('[Axios] Network-level error:', {
        message: error.message,
        config: { method: error.config?.method, url: (error.config?.baseURL || '') + (error.config?.url || '') },
        timeout: error.config?.timeout,
      });
      return Promise.reject(new Error('Network Error（可能是 CORS/证书/超时/网络）'));
    }

    // 3) 有 response 的话，按你的业务格式处理
    const { status, data, headers } = error.response;
    // 处理HTTP状态码
    if (status == 401) {
      return handleAuthFailure(error, shop)
    }
    // 处理其他业务错误
    if (data && data.code !== 200 && data.code !== 0) {
      const errorMsg = data.message || 'Request failed'
      return Promise.reject(new Error(errorMsg))
    }
    console.error('[Axios] HTTP error:', status, (error.config?.url || ''), headers, data);

    // 兼容 data 为空的情况
    const msg = (data && (data.message || data.msg)) || `HTTP ${status}`;
    return Promise.reject(new Error(msg));
  }
)


// 获取当前时间
promotion.interceptors.request.use(
  config => attachAdminAuthorization(config),
  error => Promise.reject(error)
)

promotion.interceptors.response.use(
  response => {
    const res = response.data
    if (!res || res.code !== 0) {
      if (isAuthBusinessCode(res?.code)) {
        redirectToLogin()
      }
      const details = Array.isArray(res?.error) ? res.error.filter(Boolean) : []
      const message = [res?.message || 'Request failed', ...details].filter(Boolean).join(': ')
      const err = new Error(message)
      err.details = details
      err.payload = res
      return Promise.reject(err)
    }
    return res
  },
  async error => {
    if (!error.response) {
      return Promise.reject(new Error('Network Error'))
    }

    const { status, data } = error.response
    if (status === 401) {
      return handleAuthFailure(error, promotion)
    }

    const details = Array.isArray(data?.error) ? data.error.filter(Boolean) : []
    const message = [data?.message || data?.msg || `HTTP ${status}`, ...details].filter(Boolean).join(': ')
    const err = new Error(message)
    err.details = details
    err.payload = data
    return Promise.reject(err)
  }
)

obsGateway.interceptors.request.use(
  config => attachAdminAuthorization(config),
  error => Promise.reject(error)
)

obsGateway.interceptors.response.use(
  response => {
    const res = response.data
    if (!res || res.code !== 0) {
      if (isAuthBusinessCode(res?.code)) {
        redirectToLogin()
      }
      const details = Array.isArray(res?.error) ? res.error.filter(Boolean) : []
      const message = [res?.message || 'Request failed', ...details].filter(Boolean).join(': ')
      const err = new Error(message)
      err.details = details
      err.payload = res
      return Promise.reject(err)
    }
    return res
  },
  async error => {
    if (!error.response) {
      return Promise.reject(new Error('无法连接本地 OBS 服务，请确认 http://127.0.0.1:3111 已启动'))
    }

    const { status, data } = error.response
    if (status === 401) {
      return handleAuthFailure(error, obsGateway)
    }

    const details = Array.isArray(data?.error) ? data.error.filter(Boolean) : []
    const businessCode = data?.error?.code
    const message = [data?.message || data?.msg || `HTTP ${status}`, businessCode, ...details]
      .filter(Boolean)
      .join(': ')
    const err = new Error(message)
    err.details = details
    err.payload = data
    return Promise.reject(err)
  }
)

function getExpireTime() {
  return new Date().getTime()
}

export {
  streamer,
  viewer,
  shop,
  promotion,
  obsGateway
}
